import { toast } from 'sonner'
import React, { useState } from 'react'
import { User } from '@/types/user.type'
import { Token } from '@/types/token.type'
// API & Types
import { fError } from '@/utils/format-error'
// Components
import axios, { endpoints } from '@/lib/axios'
import { fNumber } from '@/utils/format-number'
import { Button } from '@/components/ui/button'
// Hooks
import { useBoolean } from '@/hooks/use-boolean'
import { UserToken } from '@/types/user-token.type'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import { Card, CardContent } from '@/components/ui/card'
import { useDebounceText } from '@/hooks/use-debounce-text'
// Local Components
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingDialog from '@/components/share-components/loading-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import TransferForm from '../components/transfer-form'
import TransferHeaderCard from '../components/transfer-header-card'
import UserSelectionTable from '../components/user-selection-table'

type TransferType = 'user-to-user' | 'system-topup'

export default function TransferTokenView() {
  const exchangeRate = 1

  const [transferType, setTransferType] = useState<TransferType>('user-to-user')

  // Handle transfer type change with user reset
  const handleTransferTypeChange = (type: TransferType) => {
    setTransferType(type)
    // Reset selected users when transfer type changes
    setSelectedFromUser('')
    setSelectedToUser('')
    setFromUserTokens([])
    setToUserTokens([])
    setInputAmount('')
    setSelectedPercentage(null)
  }
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(
    null,
  )
  const [tokens, setTokens] = useState<Token[]>([])
  const [selectedToken, setSelectedToken] = useState<string>('')
  const [fromUserTokens, setFromUserTokens] = useState<UserToken[]>([])
  const [toUserTokens, setToUserTokens] = useState<UserToken[]>([])
  const [systemUserTokens, setSystemUserTokens] = useState<UserToken[]>([])
  const [fromUsers, setFromUsers] = useState<User[]>([])
  const [toUsers, setToUsers] = useState<User[]>([])
  const [systemUser, setSystemUser] = useState<User | null>(null)
  const [selectedFromUser, setSelectedFromUser] = useState<string>('')
  const [selectedToUser, setSelectedToUser] = useState<string>('')
  const [fromUserSearch, setFromUserSearch] = useState('')
  const [toUserSearch, setToUserSearch] = useState('')
  const [fromUserPageMeta, setFromUserPageMeta] = useState({
    total: 0,
    page: 1,
    limit: 10,
  })
  const [toUserPageMeta, setToUserPageMeta] = useState({
    total: 0,
    page: 1,
    limit: 10,
  })

  const debounceFromUserSearch = useDebounceText(fromUserSearch)
  const debounceToUserSearch = useDebounceText(toUserSearch)

  const [inputAmount, setInputAmount] = useState('')

  const initLoading = useBoolean(false)
  const swapLoading = useBoolean(false)
  const successModal = useBoolean(false)
  const confirmModal = useBoolean(false)

  // Get current user's token balance
  const getCurrentUserTokenBalance = () => {
    if (transferType === 'user-to-user' && selectedFromUser && selectedToken) {
      const userToken = fromUserTokens.find(
        (ut) => ut.userId === selectedFromUser && ut.tokenId === selectedToken,
      )
      return userToken?.amount || 0
    } else if (transferType === 'system-topup' && systemUser && selectedToken) {
      // For system topup, use actual system user token balance
      const systemUserToken = fromUserTokens.find(
        (ut) => ut.userId === systemUser.uid && ut.tokenId === selectedToken,
      )
      return systemUserToken?.amount || 0
    }
    return 0
  }

  // Get system user's token balance
  const getSystemUserTokenBalance = () => {
    if (systemUser && selectedToken) {
      const systemUserToken = systemUserTokens.find(
        (ut) => ut.userId === systemUser.uid && ut.tokenId === selectedToken,
      )
      return systemUserToken?.amount || 0
    }
    return 0
  }

  // Handle percentage selection
  const handlePercentageClick = (percentage: number) => {
    setSelectedPercentage(percentage)
    const currentBalance = getCurrentUserTokenBalance()
    const calculatedAmount = (currentBalance * percentage) / 100
    setInputAmount(fNumber(calculatedAmount).replace(/,/g, ''))
  }

  const handleMaxClick = () => {
    setSelectedPercentage(100)
    const currentBalance = getCurrentUserTokenBalance()
    setInputAmount(
      currentBalance ? fNumber(currentBalance).replace(/,/g, '') : '',
    )
  }

  // Handle manual input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (!/^\d*\.?\d*$/.test(value)) {
      return
    }
    if (value.includes('.') ? value.length > 14 : value.length > 12) {
      return
    }

    // Check if amount exceeds source balance
    const numericValue = parseFloat(value) || 0
    const currentBalance = getCurrentUserTokenBalance()

    if (numericValue > currentBalance && currentBalance > 0) {
      // Auto-set to maximum balance instead of showing error
      setInputAmount(fNumber(currentBalance).replace(/,/g, ''))
      setSelectedPercentage(100)
      toast.info(`ปรับเป็นจำนวนสูงสุด: ${fNumber(currentBalance)}`)
      return
    }

    setInputAmount(value)
    setSelectedPercentage(null)
  }

  const handleSwap = async () => {
    try {
      swapLoading.onTrue()

      if (transferType === 'user-to-user') {
        if (!selectedFromUser || !selectedToUser) {
          toast.error('กรุณาเลือกผู้ใช้ต้นทางและปลายทาง')
          return
        }

        if (selectedFromUser === selectedToUser) {
          toast.error('ไม่สามารถโอนให้ตัวเองได้')
          return
        }

        const body = {
          type: 'user-to-user',
          fromUserId: selectedFromUser,
          toUserId: selectedToUser,
          tokenId: selectedToken,
          amount: +inputAmount,
        }
        const resp = await axios.post(endpoints.tokens.transfer, body)

        if (resp) {
          successModal.onTrue()
        } else {
          toast.error('การโอนล้มเหลว')
        }
      } else if (transferType === 'system-topup') {
        if (!selectedToUser) {
          toast.error('กรุณาเลือกผู้ใช้ที่จะเติมเงิน')
          return
        }

        const body = {
          type: 'system-topup',
          toUserId: selectedToUser,
          tokenId: selectedToken,
          amount: +inputAmount,
        }
        const resp = await axios.post(
          endpoints.tokens.transferRfcFromSystem,
          body,
        )

        if (resp) {
          successModal.onTrue()
        } else {
          toast.error('การเติมเงินล้มเหลว')
        }
      }
    } catch (error) {
      let errMsg = fError(error)
      if (errMsg === 'You cannot transfer to yourself') {
        errMsg = 'ไม่สามารถโอนให้ตัวเองได้'
      }
      toast.error(errMsg)
    } finally {
      swapLoading.onFalse()
    }
  }

  const initData = async () => {
    try {
      initLoading.onTrue()

      // Fetch tokens and users data
      await Promise.all([
        fetchTokens(),
        fetchFromUsers(),
        fetchToUsers(),
        fetchSystemUser(),
      ])
    } finally {
      initLoading.onFalse()
    }
  }

  const fetchTokens = async () => {
    try {
      const { data } = await axios.get(endpoints.tokens.list, {
        params: { limit: 100 },
      })
      const tokensData = data.items || data
      setTokens(tokensData)

      // Auto-select first token if only one token available
      if (tokensData.length === 1 && !selectedToken) {
        await handleTokenSelect(tokensData[0].id)
      }
    } catch (error) {
      console.error('Error fetching tokens:', error)
      toast.error(fError(error))
      setTokens([])
    }
  }

  const fetchUserTokens = async (userId: string) => {
    try {
      const { data } = await axios.get(endpoints.userTokens.list, {
        params: { userId },
      })
      return data.items || data
    } catch (error) {
      console.error('Error fetching user tokens:', error)
      toast.error(fError(error))
      return []
    }
  }

  const fetchFromUsers = async (search = '', page = 1, limit = 10) => {
    try {
      const params: Record<string, unknown> = {
        page,
        limit,
        orderBy: 'createdAt:desc',
        isAllowSearchOnlyMemberCode: true,
      }

      if (search && search.trim() !== '') {
        params.search = search
      }

      const { data } = await axios.get(endpoints.users.list, {
        params,
      })

      setFromUsers(data.items)
      setFromUserPageMeta({
        total: data.total,
        page,
        limit,
      })
    } catch (error) {
      console.error('Error fetching from users:', error)
      toast.error(fError(error))
      setFromUsers([])
    }
  }

  const fetchToUsers = async (search = '', page = 1, limit = 10) => {
    try {
      const params: Record<string, unknown> = {
        page,
        limit,
        orderBy: 'createdAt:desc',
        isAllowSearchOnlyMemberCode: true,
      }

      if (search && search.trim() !== '') {
        params.search = search
      }

      const { data } = await axios.get(endpoints.users.list, {
        params,
      })

      setToUsers(data.items)
      setToUserPageMeta({
        total: data.total,
        page,
        limit,
      })
    } catch (error) {
      console.error('Error fetching to users:', error)
      toast.error(fError(error))
      setToUsers([])
    }
  }

  const fetchSystemUser = async () => {
    try {
      const { data } = await axios.get(endpoints.users.list, {
        params: {
          role: 'SYSTEM',
          limit: 1,
        },
      })

      if (data.items && data.items.length > 0) {
        const systemUserData = data.items[0]
        setSystemUser(systemUserData)

        // Fetch system user tokens
        try {
          const userTokensData = await axios.get(endpoints.userTokens.list, {
            params: { userId: systemUserData.uid },
          })
          const tokensData = userTokensData.data.items || userTokensData.data
          setSystemUserTokens(tokensData)
        } catch (tokenError) {
          console.error('Error fetching system user tokens:', tokenError)
          // Don't show error toast for system user tokens as it's not critical
          setSystemUserTokens([])
        }
      }
    } catch (error) {
      console.error('Error fetching system user:', error)
      toast.error(fError(error))
      setSystemUser(null)
    }
  }

  const handleFromUserSelect = async (userId: string) => {
    setSelectedFromUser(userId)
    if (userId && selectedToken) {
      const userTokens = await fetchUserTokens(userId)
      setFromUserTokens(userTokens)
    }
  }

  const handleToUserSelect = async (userId: string) => {
    setSelectedToUser(userId)
    if (userId && selectedToken) {
      const userTokens = await fetchUserTokens(userId)
      setToUserTokens(userTokens)
    }
  }

  const handleTokenSelect = async (tokenId: string) => {
    setSelectedToken(tokenId)
    setInputAmount('')
    setSelectedPercentage(null)

    // Fetch tokens for selected users if they exist
    if (selectedFromUser) {
      const userTokens = await fetchUserTokens(selectedFromUser)
      setFromUserTokens(userTokens)
    }
    if (selectedToUser) {
      const userTokens = await fetchUserTokens(selectedToUser)
      setToUserTokens(userTokens)
    }
  }

  const handleSetDefault = async () => {
    successModal.onFalse()
    setInputAmount('')
    setSelectedPercentage(null)
    setSelectedFromUser('')
    setSelectedToUser('')
    setFromUserSearch('')
    setToUserSearch('')
    setFromUserTokens([])
    setToUserTokens([])

    // Don't reset token selection and system data - just refresh user data
    await Promise.all([fetchFromUsers(), fetchToUsers()])
  }

  const handleFromUserPageChange = (page: number) => {
    setFromUserPageMeta((prev) => ({ ...prev, page }))
    fetchFromUsers(debounceFromUserSearch, page, fromUserPageMeta.limit)
  }

  const handleToUserPageChange = (page: number) => {
    setToUserPageMeta((prev) => ({ ...prev, page }))
    fetchToUsers(debounceToUserSearch, page, toUserPageMeta.limit)
  }

  useDeepEffect(() => {
    const fetchData = async () => {
      await initData()
    }

    fetchData()
  }, [])

  // Handle search changes
  useDeepEffect(() => {
    if (debounceFromUserSearch !== undefined) {
      setFromUserPageMeta((prev) => ({ ...prev, page: 1 }))
      fetchFromUsers(debounceFromUserSearch, 1, fromUserPageMeta.limit)
    }
  }, [debounceFromUserSearch])

  useDeepEffect(() => {
    if (debounceToUserSearch !== undefined) {
      setToUserPageMeta((prev) => ({ ...prev, page: 1 }))
      fetchToUsers(debounceToUserSearch, 1, toUserPageMeta.limit)
    }
  }, [debounceToUserSearch])

  const totalReceive =
    Number(inputAmount.replace(/,/g, '')) * Number(exchangeRate)

  const getSelectedUser = (userId: string) => {
    if (userId === 'SYSTEM' && systemUser) {
      return systemUser
    }
    return [...fromUsers, ...toUsers].find((u) => u.uid === userId)
  }

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    return user.displayName || '-'
  }

  const getSelectedToken = () => {
    return tokens.find((t) => t.id === selectedToken)
  }

  const getSuccessTitle = () => {
    const token = getSelectedToken()
    const tokenSymbol = token?.symbol || 'Token'

    if (transferType === 'user-to-user') {
      const fromUser = getSelectedUser(selectedFromUser)
      const toUser = getSelectedUser(selectedToUser)
      return `โอน ${fNumber(totalReceive)} ${tokenSymbol} จาก ${fromUser ? getUserDisplayName(fromUser) : '-'} ไปยัง ${toUser ? getUserDisplayName(toUser) : '-'} สำเร็จ!`
    } else {
      const toUser = getSelectedUser(selectedToUser)
      return `เติม ${fNumber(totalReceive)} ${tokenSymbol} ให้ ${toUser ? getUserDisplayName(toUser) : '-'} สำเร็จ!`
    }
  }

  const getConfirmTitle = () => {
    const token = getSelectedToken()
    const tokenSymbol = token?.symbol || 'Token'

    if (transferType === 'user-to-user') {
      const fromUser = getSelectedUser(selectedFromUser)
      const toUser = getSelectedUser(selectedToUser)
      return `คุณแน่ใจหรือไม่ที่จะโอน ${fNumber(totalReceive)} ${tokenSymbol} จาก ${fromUser ? getUserDisplayName(fromUser) : '-'} ไปยัง ${toUser ? getUserDisplayName(toUser) : '-'}?`
    } else {
      const toUser = getSelectedUser(selectedToUser)
      return `คุณแน่ใจหรือไม่ที่จะเติม ${fNumber(totalReceive)} ${tokenSymbol} ให้ ${toUser ? getUserDisplayName(toUser) : '-'}?`
    }
  }

  const successTitle = getSuccessTitle()
  const confirmTitle = getConfirmTitle()

  return (
    <div className="w-full space-y-6 mb-8">
      {/* Transfer Header */}
      <TransferHeaderCard
        transferType={transferType}
        onTransferTypeChange={handleTransferTypeChange}
        tokens={tokens}
        selectedToken={selectedToken}
        onTokenSelect={handleTokenSelect}
      />

      {systemUser && (
        <Card>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">
                    SYS
                  </span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {systemUser.displayName || 'System User'}
                  </h3>
                  <p className="text-sm text-gray-500">กระเป๋าระบบ</p>
                </div>
              </div>
              <div className="ml-auto">
                <span className="text-sm text-gray-500">
                  ยอดคงเหลือ:{' '}
                  {selectedToken
                    ? fNumber(getSystemUserTokenBalance())
                    : 'เลือกโทเคน'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Selection */}
      {transferType === 'user-to-user' && (
        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
          <div className="min-h-[400px]">
            <UserSelectionTable
              users={fromUsers}
              loading={initLoading.value}
              selectedUserId={selectedFromUser}
              onUserSelect={handleFromUserSelect}
              search={fromUserSearch}
              onSearchChange={setFromUserSearch}
              pageMeta={fromUserPageMeta}
              onPageChange={handleFromUserPageChange}
              title="เลือกผู้ใช้ต้นทาง"
              placeholder="ค้นหาผู้ใช้ต้นทางด้วยรหัสสมาชิก..."
              userTokens={fromUserTokens}
              tokens={tokens}
              selectedToken={selectedToken}
            />
          </div>

          <div className="min-h-[400px]">
            <UserSelectionTable
              users={toUsers}
              loading={initLoading.value}
              selectedUserId={selectedToUser}
              onUserSelect={handleToUserSelect}
              search={toUserSearch}
              onSearchChange={setToUserSearch}
              pageMeta={toUserPageMeta}
              onPageChange={handleToUserPageChange}
              title="เลือกผู้ใช้ปลายทาง"
              placeholder="ค้นหาผู้ใช้ปลายทางด้วยรหัสสมาชิก..."
              userTokens={toUserTokens}
              tokens={tokens}
              selectedToken={selectedToken}
            />
          </div>
        </div>
      )}

      {transferType === 'system-topup' && (
        <div className="space-y-6">
          {/* System User Info */}

          {/* User Selection */}
          <UserSelectionTable
            users={toUsers}
            loading={initLoading.value}
            selectedUserId={selectedToUser}
            onUserSelect={handleToUserSelect}
            search={toUserSearch}
            onSearchChange={setToUserSearch}
            pageMeta={toUserPageMeta}
            onPageChange={handleToUserPageChange}
            title="เลือกผู้ใช้ที่จะเติมเงิน"
            placeholder="ค้นหาผู้ใช้ที่จะเติมเงินด้วยรหัสสมาชิก..."
            userTokens={toUserTokens}
            tokens={tokens}
            selectedToken={selectedToken}
          />
        </div>
      )}

      {/* Transfer Form */}
      <Card>
        <CardContent className="pt-6">
          <TransferForm
            transferType={transferType}
            tokens={tokens}
            selectedToken={selectedToken}
            inputAmount={inputAmount}
            selectedPercentage={selectedPercentage}
            swapLoading={swapLoading.value}
            selectedFromUser={
              transferType === 'system-topup' ? 'SYSTEM' : selectedFromUser
            }
            selectedToUser={selectedToUser}
            onInputChange={handleInputChange}
            onPercentageClick={handlePercentageClick}
            onMaxClick={handleMaxClick}
            onConfirm={confirmModal.onTrue}
            getCurrentUserTokenBalance={getCurrentUserTokenBalance}
          />
        </CardContent>
      </Card>

      <LoadingDialog open={swapLoading.value} />

      {/* Confirm modal */}
      <ConfirmDialog
        open={confirmModal.value}
        title="ยืนยันการโอน"
        description={confirmTitle}
        confirmText="ยืนยัน"
        cancelText="ยกเลิก"
        onConfirm={() => {
          ;(async () => {
            confirmModal.onFalse()
            await handleSwap()
          })()
        }}
        onOpenChange={confirmModal.setValue}
      />

      {/* Success modal */}
      <Dialog open={successModal.value} onOpenChange={successModal.setValue}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{successTitle}</DialogTitle>
            <DialogDescription>
              ทำรายการแลกเปลี่ยนเสร็จสิ้นเรียบร้อยแล้ว
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleSetDefault}>ตกลง</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
